import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { encolarAccion, esFaltaDeSenal } from '@/lib/campo/colaDeAcciones'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { VisitaProgramada } from '@/lib/supabase/tipos-bd'

/**
 * LA VISITA DEL TÉCNICO ENCARGADO (0256).
 *
 * Johan: «cuando se elige un técnico debería ser como programación
 * también». Elegir quién va es programar la visita —día, hora y cuánto
 * dura— y la visita sale en los calendarios. La de certificación RETIE la
 * registra el técnico en la obra.
 */

/** Las visitas de todos, para el calendario de la oficina. */
export function useVisitasProgramadas() {
  const consulta = useQuery({
    queryKey: ['visitas-programadas'],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<VisitaProgramada[]> => {
      const { data, error } = await supabase.rpc('fn_visitas_programadas')
      if (error) throw new Error(mensajeDeError(error))
      return ((data ?? []) as VisitaProgramada[]).map((v) => ({
        ...v,
        visita_horas: v.visita_horas === null ? null : Number(v.visita_horas),
      }))
    },
  })
  return { visitas: consulta.data ?? [], cargando: consulta.isLoading }
}

/** Programar (o quitar, con colaborador null) la visita de una orden. */
export function useProgramarVisita() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (v: {
      ordenServicioId: string
      colaboradorId: string | null
      inicio: string | null
      horas: number
    }) => {
      const { error } = await supabase.rpc('fn_programar_visita', {
        p_orden_servicio_id: v.ordenServicioId,
        p_colaborador_id: v.colaboradorId,
        p_inicio: v.inicio,
        p_horas: v.horas,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await Promise.all([
        cliente.invalidateQueries({ queryKey: ['ordenes-servicio'] }),
        cliente.invalidateQueries({ queryKey: ['visitas-programadas'] }),
        cliente.invalidateQueries({ queryKey: ['mis-ordenes-por-cotizar'] }),
      ])
    },
  })
}

/**
 * El técnico registra la visita de certificación: observación y si cotizamos.
 *
 * SIN SEÑAL SE GUARDA EN EL TELÉFONO (Johan: «verifica que todo se pueda
 * hacer sin internet»). Va a la cola de acciones y sale cuando vuelva la
 * cobertura, después de sus fotos. Devuelve si quedó registrada o en cola.
 */
export function useRegistrarVisita() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (v: {
      ordenServicioId: string
      observacion: string
      quiereCotizar: boolean
    }): Promise<'registrada' | 'en_cola'> => {
      const argumentos = {
        p_orden_servicio_id: v.ordenServicioId,
        p_observacion: v.observacion,
        p_quiere_cotizar: v.quiereCotizar,
      }
      const sinRed = typeof navigator !== 'undefined' && navigator.onLine === false
      if (!sinRed) {
        const { error } = await supabase.rpc('fn_registrar_visita', argumentos)
        if (!error) return 'registrada'
        if (!esFaltaDeSenal(error)) throw new Error(mensajeDeError(error))
      }
      await encolarAccion('visita', v.ordenServicioId, argumentos)
      return 'en_cola'
    },
    onSuccess: async () => {
      await Promise.all([
        cliente.invalidateQueries({ queryKey: ['mis-ordenes-por-cotizar'] }),
        cliente.invalidateQueries({ queryKey: ['mi-programacion'] }),
      ])
    },
  })
}
